import { isLocale, Locale, LocaleEnum } from '../../types/locale';
import { Country, isCountry } from '../../types/country';

const characters = 'abcdefghijklmnopqrstuvwxyz0123456789-_'; // base 64

const getExtension = (filename: string): string => filename.split('.').pop().toLowerCase();

export const StringService = {
  generate(length = 32): string {
    return [...new Array(length)].map(() => characters[Math.floor(Math.random() * characters.length) % characters.length]).join('');
  },

  camelToKebab(string: string): string {
    return string.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
  },

  snakeToKebab(string: string): string {
    return string.replace(/_([a-z])/g, '-$1');
  },

  kebabToCamel(string: string): string {
    return string.replace(/-([a-z])/g, (_, s) => s.toUpperCase());
  },

  isVideoUrl(url: string): boolean {
    return ['mp4', 'webm', 'avi'].indexOf(getExtension(url)) >= 0;
  },

  isImageUrl(url: string): boolean {
    return ['webp', 'jpeg', 'jpg', 'png'].indexOf(getExtension(url)) >= 0;
  },

  extractCountryLocale(link?: string): { country: string | null; locale: string | null } | null {
    if (!link) return null;

    const patterns = [
      /^([a-zA-Z]{2,3})-([a-zA-Z]{2,3})(?:\/|$)/, // Matches "at-de" and "at-de/..."
      /^([a-zA-Z]{2})\/([a-zA-Z]{2})(?:\/|$)/, // Matches "at/de" and "at/de/..."
    ];

    const match = patterns.map((pattern) => link.match(pattern)).find(Boolean);

    if (!match) return null;

    const [, country, locale] = match;

    return isCountry(country) && isLocale(locale) ? { country, locale } : null;
  },

  getCountryLocaleInfo(link?: string): { countryLocaleCode: string | null; country: Country | null; locale: Locale | null } | null {
    // Remove the leading slash (if present)
    const normalizedLink = link.replace(/^\/+/, '');
    const result = this.extractCountryLocale(normalizedLink);
    return result ? { countryLocaleCode: `${result.country}-${result.locale}`, ...result } : { countryLocaleCode: null, country: null, locale: null };
  },

  hasCountryLocaleInfo(link?: string): boolean {
    // Remove the leading slash (if present)
    const normalizedLink = link.replace(/^\/+/, '');
    return this.extractCountryLocale(normalizedLink) !== null;
  },

  hasLocaleInfo(link?: string): boolean {
    if (!link) {
      return false;
    }

    // Remove the leading slash (if present)
    const normalizedLink = link.replace(/^\/+/, '');

    const locales = Object.values(LocaleEnum);
    const localePattern = `^(${locales.join('|')})(?:\\/|$)`;
    const localeRegex = new RegExp(localePattern);

    return localeRegex.test(normalizedLink);
  },
};
